import React, { useContext, useMemo, useState, useCallback } from "react";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";

import { Button, Table, OverflowTooltip } from "components";
import { getFormattedDate, hasPermissions, permissionList } from "helpers";
import {
	AttachmentDocumentName,
	GetAttachmentImagePath,
} from "helpers/enums-helper";
import { uiActions } from "store";
import { getCurrentUserId } from "store/user/user.reducer";
import { DocumentMutation } from "types";

import DocumentsServiceContext from "./documentsServiceContext";
import useDocumentsService from "./useDocumentsService";

import { ReactComponent as RemoveImage } from "static/images/cancel.svg";
import { ReactComponent as CopyIcon } from "static/images/copy_icon.svg";
import { ReactComponent as DownloadImage } from "static/images/downloadFile.svg";
import { ReactComponent as ShowImage } from "static/images/eye.svg";
import styles from "./documentsTable.module.scss";

export type DocumentsTableProps = {
	entityId: string | number;
	sectionTitle?: string;
	onDeleteDocument: Function;
	onViewDocument: Function;
	onDownloadDocument: Function;
	sectionClassName?: string;
	AddDocumentButton?: React.FC<{ addDocument: DocumentMutation }>;
	showLinkColumn?: boolean;
	columns?: {
		Header: string;
		accessor?: string;
		id?: string;
		disableSortBy?: boolean;
		classNameHeader?: string;
		classNameCell?: string;
		Cell?: Function;
	}[];
};

const DocumentsTable = ({
	entityId,
	AddDocumentButton,
	sectionClassName,
	sectionTitle,
	onDeleteDocument,
	onViewDocument,
	onDownloadDocument,
	columns,
	showLinkColumn = false,
}: DocumentsTableProps) => {
	const dispatch = useDispatch();

	const [sortBy, setSortBy] = useState({ id: "createdAt", desc: false });
	const [page, setPage] = useState(0);

	const currentUserId = useSelector(getCurrentUserId);

	const { key, service } = useContext(DocumentsServiceContext);
	const {
		useDocumentsListQuery,
		useAddDocumentMutation,
		useDeleteDocumentMutation,
		useViewDocumentMutation,
		useDownloadDocumentMutation,
	} = useDocumentsService({ key, service });

	const { data, isFetching, refetch } = useDocumentsListQuery({
		entityId,
		page,
		sortBy,
	});

	const { mutateAsync: addDocument } = useAddDocumentMutation(page, sortBy);
	const { mutateAsync: deleteDocument } = useDeleteDocumentMutation(refetch);
	const { mutate: viewDocument } = useViewDocumentMutation();
	const { mutate: downloadDocument } = useDownloadDocumentMutation();

	const meta = useMemo(
		() => ({
			loading: isFetching,
			totalItems: data?.count || 0,
		}),
		[isFetching, data?.count],
	);

	const tableData = useMemo(() => {
		if (Array.isArray(data)) {
			return data;
		} else if (data?.items) {
			return data.items;
		} else {
			return [];
		}
	}, [data]);

	const hasPermissionDelete = useMemo(
		() =>
			hasPermissions([
				permissionList.WithdrawAttachmentsCreatedByAnyUser,
			]),
		[],
	);

	const itemCount = useCallback(
		(numberItem: number) => {
			if (page === 0) return numberItem;
			else if (page > 0) return 50 * page + numberItem;
		},
		[page],
	);

	const handlePageChange = useCallback(
		({ pageIndex }) => {
			if (pageIndex !== page) {
				setPage(pageIndex);
			}
		},
		[page],
	);

	const handleSortChange = useCallback(({ sortBy }) => {
		setSortBy(sortBy[0]);
	}, []);

	const handleCopyClick = useCallback(
		text => {
			navigator.clipboard.writeText(text).then(() =>
				dispatch(
					uiActions.showInfoNotification({
						title: "E-Signature link has been copied",
					}),
				),
			);
		},
		[dispatch],
	);

	const defaultColumns = useMemo(
		() => [
			{
				Header: "",
				accessor: "number",
				disableSortBy: true,
				classNameCell: styles.numberCol,
				classNameHeader: styles.numberCol,
				Cell({ row }) {
					return <div>{itemCount(parseInt(row.id) + 1)}</div>;
				},
			},
			{
				Header: "",
				accessor: "iconType",
				disableSortBy: true,
				classNameCell: styles.iconTypeCol,
				classNameHeader: styles.iconTypeCol,
				Cell({ row }) {
					return (
						<img
							src={GetAttachmentImagePath(row.original.type)}
							alt=""
							width="20"
							height="15"
							className="mr-3 pl-1"
						/>
					);
				},
			},
			{
				Header: "Name",
				accessor: "name",
				disableSortBy: true,
				Cell({ cell, row: { original } }) {
					return (
						<div
							className={`cellWithIcon cut-text-laptop ${
								original?.deleted_at ? styles.crossed : ""
							}`}
						>
							<OverflowTooltip onlyOnLaptop title={cell?.value}>
								{cell?.value}
							</OverflowTooltip>
						</div>
					);
				},
			},
			{
				Header: "Type",
				accessor: "type",
				disableSortBy: true,
				Cell({ cell }) {
					return (
						<OverflowTooltip
							onlyOnLaptop
							title={AttachmentDocumentName(cell.value)}
						>
							{AttachmentDocumentName(cell.value)}
						</OverflowTooltip>
					);
				},
			},
			{
				Header: "Timestamp",
				accessor: "created_at",
				id: "createdAt",
				Cell({ cell }) {
					const FORMAT = "DD MMMM YYYY h:mm A";
					const createdAt = getFormattedDate({
						date: cell.value,
						format: FORMAT,
					});
					return (
						<OverflowTooltip onlyOnLaptop title={createdAt}>
							{createdAt}
						</OverflowTooltip>
					);
				},
			},
		],
		[itemCount],
	);

	const actionsColumn = useMemo(
		() => [
			{
				Header: "Actions",
				accessor: "",
				id: "documentActions",
				disableSortBy: true,
				classNameHeader: styles.actionsCellHeader,
				classNameCell: styles.actionsCell,
				Cell({ row: { original } }) {
					return (
						<div>
							<Button
								onClick={() =>
									onViewDocument({
										document: original,
										viewDocument,
									})
								}
								icon={<ShowImage />}
								btnIcon
								variant="secondary"
								title="View Document"
							/>
							<Button
								onClick={() =>
									onDownloadDocument({
										document: original,
										downloadDocument,
									})
								}
								icon={<DownloadImage />}
								btnIcon
								variant="secondary"
								title="Download Document"
							/>
							<Button
								onClick={() =>
									onDeleteDocument({
										document: original,
										deleteDocument,
									})
								}
								icon={<RemoveImage />}
								btnIcon
								variant="secondary"
								title="Remove Document"
								size="sm"
								style={{ marginTop: "3px" }}
								disabled={
									original?.deleted_at ||
									(currentUserId !==
										original?.created_by_id &&
										!hasPermissionDelete)
								}
							/>
						</div>
					);
				},
			},
		],
		[
			currentUserId,
			deleteDocument,
			downloadDocument,
			hasPermissionDelete,
			onDeleteDocument,
			onDownloadDocument,
			onViewDocument,
			viewDocument,
		],
	);

	const linkColumn = useMemo(
		() =>
			showLinkColumn
				? [
						{
							Header: "E-Signature Link",
							accessor: "signature_url",
							disableSortBy: true,
							classNameHeader: styles.linkCellColumn,
							classNameCell: styles.linkCellColumn,
							Cell({ value }) {
								return (
									value && (
										<div className={styles.linkWrapper}>
											<div className="cut-text">
												<a
													href={value}
													target="_blank"
													rel="noreferrer"
												>
													{value}
												</a>
											</div>

											<Button
												btnIcon
												onClick={() =>
													handleCopyClick(value)
												}
												icon={<CopyIcon />}
												title="Copy"
												variant="secondary"
												size="sm"
												className={styles.copyBtn}
											/>
											<i className="column-separator" />
										</div>
									)
								);
							},
						},
				  ]
				: [],
		[handleCopyClick, showLinkColumn],
	);

	const correctedColumns = [
		...(columns || defaultColumns),
		...linkColumn,
		...actionsColumn,
	];

	return (
		<div
			className={clsx(styles.wrappAttachments, sectionClassName, "mt-4")}
		>
			<div className={styles.sectionActions}>
				{sectionTitle && (
					<h6 className={styles.sectionTitle}>{sectionTitle}</h6>
				)}
				{AddDocumentButton && (
					<AddDocumentButton addDocument={addDocument} />
				)}
			</div>
			<Table
				meta={meta}
				className="vh-80"
				columns={correctedColumns}
				data={tableData}
				handlePageChange={handlePageChange}
				handleSortChange={handleSortChange}
				initialState={{ sortBy: [sortBy], pageIndex: page }}
				compact
			/>
		</div>
	);
};

export default DocumentsTable;
